import { AxiosResponse } from 'axios';

import { IGetSimpleListFilters, IGetSimpleListResponse } from 'models/http/common';
import { IGetHistoryResponse } from 'models/http/history';
import {
  ICancelSuspendVehicleResponse,
  ICreateVehicleRequest,
  ICreateVehicleResponse,
  IDeleteVehicleFilters,
  IDeleteVehicleResponse,
  IGetVehicleMakeModelsResponse,
  IGetVehicleMakesResponse,
  IGetSingleVehicleResponse,
  IGetVehicleFilters,
  IGetVehiclesEVConfigResponse,
  IGetVehiclesResponse,
  ISuspendVehicleResponse,
  IUpdateVehicleRequest,
  IUpdateVehicleResponse,
} from 'models/http/vehicle';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import { HTTP } from './core.http';
import DataUploadBaseHttp from './utils/data-upload.http';

class VehicleHttp extends DataUploadBaseHttp implements HTTP<IDeleteVehicleFilters> {
  public static DataExportRoute = `${ModelsNames.Vehicles}/data-export`;
  protected readonly route: DataUploadModels = ModelsNames.Vehicles;

  public async getSimple(_?: IGetSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    return serverAxios
      .get(`/${this.route}/simple?`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async get(filters?: IGetVehicleFilters): Promise<IGetVehiclesResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetVehiclesResponse>) => {
        return response.data;
      });
  }

  public async getById(id: number, abortController?: AbortController): Promise<IGetSingleVehicleResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleVehicleResponse>) => {
        return response.data;
      });
  }

  public async getMakes(abortController?: AbortController): Promise<IGetVehicleMakesResponse> {
    return serverAxios
      .get(`/${this.route}/makes`, { signal: abortController?.signal, timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IGetVehicleMakesResponse>) => response.data);
  }

  public async getMakeModels(make: string, abortController?: AbortController): Promise<IGetVehicleMakeModelsResponse> {
    return serverAxios
      .get(`/${this.route}/models/${make}`, { signal: abortController?.signal, timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IGetVehicleMakeModelsResponse>) => response.data);
  }

  public async getEVConfig(ids: number[], abortController?: AbortController): Promise<IGetVehiclesEVConfigResponse['records']> {
    const queryParams = prepareQPFromFilters({ ids });

    return serverAxios
      .get(`/${this.route}/ev-config?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetVehiclesEVConfigResponse>) => {
        return response.data.records;
      });
  }

  public async create(data: ICreateVehicleRequest): Promise<ICreateVehicleResponse> {
    return serverAxios
      .post(`/${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateVehicleResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, data: IUpdateVehicleRequest): Promise<IUpdateVehicleResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateVehicleResponse>) => {
        return response.data;
      });
  }

  public async suspend(id: number, reason: string): Promise<ISuspendVehicleResponse> {
    return serverAxios.put(`/${this.route}/${id}/suspension`, { reason }).then((response: AxiosResponse<ISuspendVehicleResponse>) => {
      return response.data;
    });
  }

  public async unsuspend(id: number): Promise<ICancelSuspendVehicleResponse> {
    return serverAxios.delete(`/${this.route}/${id}/suspension`).then((response: AxiosResponse<ICancelSuspendVehicleResponse>) => {
      return response.data;
    });
  }

  public async delete(ids: number[]): Promise<IDeleteVehicleResponse>;
  public async delete(filters: IDeleteVehicleFilters): Promise<IDeleteVehicleResponse>;
  public async delete(filters: number[] | IDeleteVehicleFilters): Promise<IDeleteVehicleResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteVehicleResponse>) => {
        return response.data;
      });
  }
}

export default VehicleHttp;
